import React from 'react';
import { Link } from 'react-router-dom';
import { useLocaleContext } from '../../context/LocaleContext';
import { locales, urlMappings } from '../../utils/hreflang';
import { toolsSections } from '../../utils/navigationData';
import { getTranslation } from '../../utils/translations';
import * as Icons from 'lucide-react';
import NavDropdown from './NavDropdown';

const ToolsDropdown: React.FC = () => {
  const { locale } = useLocaleContext();

  // Get locale-aware path (same pattern as Footer)
  const getLocalePath = (path: string) => {
    const prefix = locales[locale] || '';
    const mappings = urlMappings[path];
    if (mappings && mappings[locale]) {
      return `${prefix}${mappings[locale]}`;
    }
    return `${prefix}${path}`;
  };

  // Get icon component dynamically
  const getIcon = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName];
    return IconComponent ? <IconComponent size={20} aria-hidden="true" /> : null;
  };

  // Translation key mapping for titles and descriptions
  const translationKeys: Record<string, { title: string; desc: string }> = {
    'ChatGPT Watermarks': { title: 'nav.chatgptWatermarks', desc: 'nav.chatgptWatermarksDesc' },
    'Gemini Watermarks': { title: 'nav.geminiWatermarks', desc: 'nav.geminiWatermarksDesc' },
    'Claude Watermarks': { title: 'nav.claudeWatermarks', desc: 'nav.claudeWatermarksDesc' },
    'Gemini Images': { title: 'nav.geminiImages', desc: 'nav.geminiImagesDesc' },
    'DOCX Cleaner': { title: 'nav.docxCleaner', desc: 'nav.docxCleanerDesc' },
    'PDF Cleaner': { title: 'nav.pdfCleaner', desc: 'nav.pdfCleanerDesc' },
    'Pages Cleaner': { title: 'nav.pagesCleaner', desc: 'nav.pagesCleanerDesc' },
    'Chrome Extension': { title: 'nav.chromeExtension', desc: 'nav.chromeExtensionDesc' },
    'For Students': { title: 'nav.forStudents', desc: 'nav.forStudentsDesc' },
  };

  const sectionTranslationKeys: Record<string, string> = {
    'TEXT WATERMARK REMOVAL': 'nav.textWatermarkRemoval',
    'IMAGE WATERMARK REMOVAL': 'nav.imageWatermarkRemoval',
    'DOCUMENT TOOLS': 'nav.documentTools',
    'EXTENSIONS': 'nav.extensions',
  };

  return (
    <NavDropdown trigger={getTranslation(locale, 'nav.tools')} width="w-[560px]" align="left">
      <div className="grid grid-cols-2 gap-4 p-4">
        {toolsSections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            {/* Section Header */}
            <h3 className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-2 px-2">
              {getTranslation(locale, sectionTranslationKeys[section.title] || '')}
            </h3>

            {/* Section Items */}
            <div className="space-y-1">
              {section.items.map((item, itemIndex) => {
                const isExternal = item.path.startsWith('http');
                const translationKey = translationKeys[item.title];
                const title = translationKey ? getTranslation(locale, translationKey.title) : item.title;
                const description = translationKey ? getTranslation(locale, translationKey.desc) : item.description;

                if (isExternal) {
                  return (
                    <a
                      key={itemIndex}
                      href={item.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 px-2 py-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors group"
                      role="menuitem"
                    >
                      <div className="flex-shrink-0 mt-0.5 text-neutral-600 dark:text-neutral-400 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors">
                        {getIcon(item.icon || 'Circle')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-neutral-900 dark:text-white group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors">
                          {title}
                        </div>
                        {description && (
                          <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
                            {description}
                          </div>
                        )}
                      </div>
                    </a>
                  );
                }

                return (
                  <Link
                    key={itemIndex}
                    to={getLocalePath(item.path)}
                    className="flex items-start gap-3 px-2 py-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors group"
                    role="menuitem"
                  >
                    <div className="flex-shrink-0 mt-0.5 text-neutral-600 dark:text-neutral-400 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors">
                      {getIcon(item.icon || 'Circle')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-900 dark:text-white group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors">
                        {title}
                      </div>
                      {description && (
                        <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
                          {description}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </NavDropdown>
  );
};

export default React.memo(ToolsDropdown);
